import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { devAuthWhoami } from '../../features/dev/api';
import { hasAdminReviewToken } from '../../shared/adminReviewAuth';
import { canAccessInternalRoutes } from '../../shared/routeAccess';
import AuthContext from '../../contexts/AuthContext';
import type { RouteGuardKind, RouteGuardStatus } from './types';

/**
 * Resolves whether the current route guard allows rendering children.
 * Pass `revision` to re-run sync guards after login (e.g. admin token saved).
 */
export function useRouteGuard(
  guard: RouteGuardKind,
  revision = 0,
): RouteGuardStatus {
  const { isAuthenticated, isLoading: authLoading } = useContext(AuthContext);

  const [adminAllowed, setAdminAllowed] = useState<boolean>(() =>
    guard === 'admin' ? hasAdminReviewToken() : false,
  );

  useEffect(() => {
    if (guard === 'admin') {
      setAdminAllowed(hasAdminReviewToken());
    }
  }, [guard, revision]);

  const devWhoami = useQuery({
    queryKey: ['dev', 'whoami', 'route-guard'],
    queryFn: devAuthWhoami,
    enabled: guard === 'dev',
    retry: false,
    staleTime: 0,
  });

  if (guard === 'auth') {
    if (authLoading) return 'loading';
    return isAuthenticated ? 'allowed' : 'denied';
  }

  if (guard === 'internal') {
    return canAccessInternalRoutes() ? 'allowed' : 'denied';
  }

  if (guard === 'admin') {
    return adminAllowed ? 'allowed' : 'denied';
  }

  if (guard === 'dev') {
    if (devWhoami.isLoading) {
      return 'loading';
    }
    return devWhoami.data?.authenticated ? 'allowed' : 'denied';
  }

  return 'denied';
}
